// Pure helpers for the Team health (Occupancy) report
// NOTE: deliberately import-free so scripts/verify-team-health.mjs can transpile and drive the real module
// NOTE: every calendar date is a UTC 'YYYY-MM-DD' string, the same calendar the backend measures on

export type TeamHealthView = 'monthly' | 'weekly' | 'quarterly';

export interface TeamHealthPeriod {
    fromDate: string;
    toDate: string;
}

export interface TeamHealthWeek extends TeamHealthPeriod {
    label: string;
}

export interface TeamHealthQuarter extends TeamHealthPeriod {
    // Select value, e.g. '2026-Q4'
    value: string;

    // Shown label, e.g. '2026 Q4'
    label: string;
}

// First load, and anything unreadable in storage, opens the Monthly view
export const DEFAULT_TEAM_HEALTH_VIEW: TeamHealthView = 'monthly';

// Longest period the backend accepts, both ends included
export const MAX_PERIOD_DAY_COUNT = 366;

// Weekly view opens on the current week plus this many preceding weeks
export const DEFAULT_PRECEDING_WEEK_COUNT = 5;

// Quarterly view offers the current quarter plus this many preceding quarters
export const SELECTABLE_PRECEDING_QUARTER_COUNT = 11;

// Quarterly view opens on the current quarter plus this many preceding quarters
export const DEFAULT_PRECEDING_QUARTER_COUNT = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const parseTeamHealthView = (value: string | null | undefined): TeamHealthView => {
    if (value === 'monthly' || value === 'weekly' || value === 'quarterly') {
        return value;
    }

    return DEFAULT_TEAM_HEALTH_VIEW;
};

export const toUtcDateString = (date: Date) => date.toISOString().slice(0, 10);

// Strict 'YYYY-MM-DD' parse; overflowing dates such as 2026-02-30 are refused
export const parseUtcDate = (value: string): Date | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return null;
    }

    const date = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime()) || toUtcDateString(date) !== value) {
        return null;
    }

    return date;
};

export const addUtcDays = (value: string, days: number) => {
    const date = parseUtcDate(value);
    if (!date) {
        return value;
    }

    return toUtcDateString(new Date(date.getTime() + days * DAY_MS));
};

// Calendar days between two dates, both ends included
export const countPeriodDays = (fromDate: string, toDate: string) => {
    const from = parseUtcDate(fromDate);
    const to = parseUtcDate(toDate);
    if (!from || !to) {
        return 0;
    }

    return Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1;
};

// ------------------------------------------------------------------------------------ Monthly

// Month as 'YYYY-MM', the value of a native month input
export const getUtcMonthValue = (now: Date) => toUtcDateString(now).slice(0, 7);

export const getUtcMonthPeriod = (monthValue: string): TeamHealthPeriod | null => {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(monthValue);
    if (!match) {
        return null;
    }

    // Day 0 of the following month is the last day of this one
    const lastDay = new Date(Date.UTC(Number(match[1]), Number(match[2]), 0));

    return { fromDate: `${monthValue}-01`, toDate: toUtcDateString(lastDay) };
};

// Historical months are all selectable; months after the current UTC month are not
export const isSelectableMonth = (monthValue: string, now: Date) =>
    getUtcMonthPeriod(monthValue) !== null && monthValue <= getUtcMonthValue(now);

export const formatMonthLabel = (monthValue: string) => {
    const period = getUtcMonthPeriod(monthValue);
    if (!period) {
        return monthValue;
    }

    return `${MONTH_NAMES[Number(monthValue.slice(5, 7)) - 1]} ${monthValue.slice(0, 4)}`;
};

// ------------------------------------------------------------------------------------- Weekly

// Monday of the week a date belongs to
export const getUtcWeekStart = (value: string) => {
    const date = parseUtcDate(value);
    if (!date) {
        return value;
    }

    // getUTCDay() is 0 on Sunday through 6 on Saturday
    const offset = (date.getUTCDay() + 6) % 7;

    return addUtcDays(value, -offset);
};

// Sunday of the week a date belongs to
export const getUtcWeekEnd = (value: string) => addUtcDays(getUtcWeekStart(value), 6);

export const getDefaultWeeklyPeriod = (now: Date): TeamHealthPeriod => {
    const currentWeekStart = getUtcWeekStart(toUtcDateString(now));

    return {
        fromDate: addUtcDays(currentWeekStart, -7 * DEFAULT_PRECEDING_WEEK_COUNT),
        toDate: addUtcDays(currentWeekStart, 6),
    };
};

// Widen a range to whole Monday-through-Sunday weeks
export const snapToWholeWeeks = (period: TeamHealthPeriod): TeamHealthPeriod => ({
    fromDate: getUtcWeekStart(period.fromDate),
    toDate: getUtcWeekEnd(period.toDate),
});

// Validation message for a requested period, or null when the backend will accept it
export const validatePeriod = (period: TeamHealthPeriod): string | null => {
    if (!parseUtcDate(period.fromDate) || !parseUtcDate(period.toDate)) {
        return 'Choose both a From date and a To date.';
    }

    if (period.toDate < period.fromDate) {
        return 'The To date must not be before the From date.';
    }

    if (countPeriodDays(period.fromDate, period.toDate) > MAX_PERIOD_DAY_COUNT) {
        return `The selected weeks must not span more than ${MAX_PERIOD_DAY_COUNT} days.`;
    }

    return null;
};

// Week label shows the work days it is measured on, e.g. 'Jul 27 – 31' or 'Aug 31 – Sep 4'
export const formatWeekLabel = (weekStart: string) => {
    const friday = addUtcDays(weekStart, 4);
    const startMonth = MONTH_NAMES[Number(weekStart.slice(5, 7)) - 1].slice(0, 3);
    const endMonth = MONTH_NAMES[Number(friday.slice(5, 7)) - 1].slice(0, 3);
    const startDay = Number(weekStart.slice(8, 10));
    const endDay = Number(friday.slice(8, 10));

    if (startMonth === endMonth) {
        return `${startMonth} ${startDay} – ${endDay}`;
    }

    return `${startMonth} ${startDay} – ${endMonth} ${endDay}`;
};

// Whole Monday-through-Sunday weeks covering a period, in order
export const listWeeks = (period: TeamHealthPeriod): TeamHealthWeek[] => {
    const snapped = snapToWholeWeeks(period);
    if (validatePeriod(snapped) !== null) {
        return [];
    }

    const weeks: TeamHealthWeek[] = [];
    for (let start = snapped.fromDate; start <= snapped.toDate; start = addUtcDays(start, 7)) {
        weeks.push({ fromDate: start, toDate: addUtcDays(start, 6), label: formatWeekLabel(start) });
    }

    return weeks;
};

// ---------------------------------------------------------------------------------- Quarterly

// Quarters are numbered continuously (year * 4 + 0..3), so a range is plain integer arithmetic
export const getUtcQuarterIndex = (now: Date) => now.getUTCFullYear() * 4 + Math.floor(now.getUTCMonth() / 3);

// Whole UTC calendar quarter: Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec, both ends included
export const getQuarterByIndex = (index: number): TeamHealthQuarter => {
    const year = Math.floor(index / 4);
    const quarter = index - year * 4;

    // Day 0 of the month after the quarter is the last day of the quarter
    const firstDay = new Date(Date.UTC(year, quarter * 3, 1));
    const lastDay = new Date(Date.UTC(year, quarter * 3 + 3, 0));

    return {
        value: `${year}-Q${quarter + 1}`,
        label: `${year} Q${quarter + 1}`,
        fromDate: toUtcDateString(firstDay),
        toDate: toUtcDateString(lastDay),
    };
};

// Strict 'YYYY-Qn' parse to a quarter index, or null
export const parseQuarterValue = (value: string): number | null => {
    const match = /^(\d{4})-Q([1-4])$/.exec(value);
    if (!match) {
        return null;
    }

    return Number(match[1]) * 4 + Number(match[2]) - 1;
};

// Current UTC quarter first, then the preceding ones; quarters after the current one are not offered
export const listSelectableQuarters = (now: Date): TeamHealthQuarter[] => {
    const currentIndex = getUtcQuarterIndex(now);

    const quarters: TeamHealthQuarter[] = [];
    for (let offset = 0; offset <= SELECTABLE_PRECEDING_QUARTER_COUNT; offset += 1) {
        quarters.push(getQuarterByIndex(currentIndex - offset));
    }

    return quarters;
};

export const getDefaultQuarterRange = (now: Date) => {
    const currentIndex = getUtcQuarterIndex(now);

    return {
        fromValue: getQuarterByIndex(currentIndex - DEFAULT_PRECEDING_QUARTER_COUNT).value,
        toValue: getQuarterByIndex(currentIndex).value,
    };
};

// Validation message for a requested quarter range, or null when every quarter in it can be requested
export const validateQuarterRange = (fromValue: string, toValue: string): string | null => {
    const fromIndex = parseQuarterValue(fromValue);
    const toIndex = parseQuarterValue(toValue);
    if (fromIndex === null || toIndex === null) {
        return 'Choose both a From Quarter and a To Quarter.';
    }

    if (toIndex < fromIndex) {
        return 'The To Quarter must not be before the From Quarter.';
    }

    return null;
};

// Every quarter of a range, in order; each is requested on its own, never as one combined period
export const listQuarters = (fromValue: string, toValue: string): TeamHealthQuarter[] => {
    const fromIndex = parseQuarterValue(fromValue);
    const toIndex = parseQuarterValue(toValue);
    if (fromIndex === null || toIndex === null || validateQuarterRange(fromValue, toValue) !== null) {
        return [];
    }

    const quarters: TeamHealthQuarter[] = [];
    for (let index = fromIndex; index <= toIndex; index += 1) {
        quarters.push(getQuarterByIndex(index));
    }

    return quarters;
};

// ------------------------------------------------------------------------------ Aggregations

// Unweighted mean of team scores, the same rule the All teams page uses; null when there is none
export const calculateOverallScore = (scores: number[]) => {
    if (scores.length === 0) {
        return null;
    }

    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

// Highest occupancy first; ties keep a stable, readable order
export const sortByScoreDescending = <T extends { score: number; title: string }>(items: T[]) =>
    [...items].sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

export const compareByFirstName = (
    a: { id: string; first_name: string; last_name: string },
    b: { id: string; first_name: string; last_name: string }
) =>
    a.first_name.localeCompare(b.first_name, undefined, { sensitivity: 'base' }) ||
    a.last_name.localeCompare(b.last_name, undefined, { sensitivity: 'base' }) ||
    a.id.localeCompare(b.id);

// Clicking the selected team again, or Overall Capacity (null), clears the filter
export const toggleTeamSelection = (selectedTeamId: string | null, clickedTeamId: string | null) => {
    if (clickedTeamId === null || clickedTeamId === selectedTeamId) {
        return null;
    }

    return clickedTeamId;
};

export interface TeamHealthMember<U> {
    user: U;
    teams: Array<{ id: string; title: string }>;
}

// Every active participant of the given teams, once, with every team they are on, alphabetical
export const collectMembers = <U extends { id: string; first_name: string; last_name: string }>(
    teams: Array<{ id: string; title: string; participants: Array<{ user: U }> }>
): TeamHealthMember<U>[] => {
    const members = new Map<string, TeamHealthMember<U>>();
    teams.forEach((team) => {
        team.participants.forEach(({ user }) => {
            const member = members.get(user.id) ?? { user, teams: [] };
            if (!member.teams.some((t) => t.id === team.id)) {
                member.teams.push({ id: team.id, title: team.title });
            }
            members.set(user.id, member);
        });
    });

    return Array.from(members.values()).sort((a, b) => compareByFirstName(a.user, b.user));
};

export const filterMembersByTeam = <M extends { teams: Array<{ id: string }> }>(members: M[], teamId: string | null) =>
    teamId === null ? members : members.filter((member) => member.teams.some((team) => team.id === teamId));

// A member's score is their own; it is the same on every team they are on. Absent means not scored
export const buildParticipantScoreMap = (
    items: Array<{ participants?: Array<{ user_id: string; score: number }> }>
) => {
    const scores: Record<string, number> = {};
    items.forEach((item) => {
        (item.participants ?? []).forEach((participant) => {
            if (!(participant.user_id in scores)) {
                scores[participant.user_id] = participant.score;
            }
        });
    });

    return scores;
};

// Weekly bar label: whole percent rounded up (0.0401 → 5%); the tolerance keeps 0.14 at 14%, not 15%
export const formatCeilPercentage = (score: number) => `${Math.ceil(score * 100 - 1e-9)}%`;

// Period data is only rendered when every item says it answers the period that was asked for
export const isPeriodEcho = (items: Array<{ from_date?: string; to_date?: string }>, period: TeamHealthPeriod) =>
    items.every((item) => item.from_date === period.fromDate && item.to_date === period.toDate);
